import React, { useState, useEffect, useMemo } from "react";
// Using date-fns for robust date handling
import {
  endOfDay,
  format,
  format as formatDateFns,
  isToday,
  startOfDay,
} from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";
import { Button } from "../../components/button";
import { Card, CardContent } from "../../components/card"; // Card still useful for structure
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/select";
import { Textarea } from "../../components/textarea";
import { Badge } from "../../components/badge";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../../components/popover";
import { Calendar as CalendarComponent } from "../../components/calendar";
import { RadioGroup, RadioGroupItem } from "../../components/radio-group";
import { Label } from "../../components/label";
import {
  Clock,
  Phone,
  Video,
  Calendar as CalendarIcon,
  Copy,
  CheckCircle,
  ExternalLink,
  User,
  Bell,
  Smile,
  CalendarCheck, // For Go to Today button
  Clipboard,
  ListFilter,
  FileText,
} from "lucide-react";
import { getCalendlyEvents, getMissedEvents } from "../../api/calendly";
import { useSelector } from "react-redux";
import { getSimplePracticeEvents } from "../../api/calendly";
import {
  getLeadInfoAPI,
  createActivityOnLeadAPI,
  getLeadActivityReport,
  getLeadFromName,
  sendSlackMessagePaidDisposition,
} from "../../api/leadsquared";

import { toast } from "react-hot-toast";
import {
  activityEvents,
  DISPOSITION_IDS_TO_NOT_SHOW,
} from "./options/dispositions";
import { modalityOptions } from "./options/modality";
import { diagnosisOptions } from "./options/diagnosis";
import {
  createTherapistNoteAPI,
  getAllTherapistNotesForPatient,
} from "../../api/notes";
import { useSearchParams } from "react-router-dom";
import { mysqlDatetimeToJavascriptDatetime } from "../../utils/helpers";
import { THERAPIST_FFS_EVENTS } from "./options/therapist";

// --- Utility Functions (Unchanged) ---

function formatTimeRange(startTime, endTime) {
  const start = format(new Date(startTime), "hh:mm a");
  const end = format(new Date(endTime), "hh:mm a");
  if (start === "Invalid Date" || end === "Invalid Date") {
    return "Invalid Time";
  }
  return `${start} - ${end}`;
}

function getAppointmentStatus(startTime, endTime) {
  try {
    const now = new Date(); // Current time
    const start = new Date(startTime);
    const end = new Date(endTime);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return "invalid";
    if (now >= start && now <= end) return "current";
    if (now < start) return "upcoming";
    return "past"; // now > end
  } catch (error) {
    console.error("Error getting appointment status:", error);
    return "invalid";
  }
}

function sortAppointmentsByTime(appointments) {
  const validAppointments = appointments.filter(
    (a) => a.startTime && !isNaN(new Date(a.startTime).getTime())
  );
  return [...validAppointments].sort(
    (a, b) => new Date(a.startTime) - new Date(b.startTime)
  );
}

function sortActivitiesByTime(activities) {
  const validActivities = activities.filter(
    (a) =>
      a.CreatedOn &&
      !isNaN(new Date(a.CreatedOn).getTime()) &&
      !DISPOSITION_IDS_TO_NOT_SHOW.includes(a.EventCode)
  );
  return [...validActivities].sort(
    (a, b) => new Date(b.CreatedOn) - new Date(a.CreatedOn)
  );
}

// --- Render Appointment Card ---
const RenderAppointmentCard = ({
  appointment,
  upcomingAppointmentId,
  handleJoinZoomClick,
  isCopied,
  handleCopyPhone,
  handlePatientDetailsClick,
  handleDispositionClick,
  handleSimplePracticeClick,
}) => {
  const status = getAppointmentStatus(
    appointment.startTime,
    appointment.endTime
  );
  const isCurrent = status === "current";
  const isUpcomingNext =
    status === "upcoming" && appointment.id === upcomingAppointmentId;
  const isPast = status === "past";
  const isOtherUpcoming = status === "upcoming" && !isUpcomingNext;

  // Using a consistent color palette without green/blue distinction
  let cardClasses =
    "bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden"; // Base card
  let timeBoxClasses = "p-4 md:w-60 flex flex-col justify-center"; // Smaller timebox
  let indicatorBorderClass = "border-l-4"; // Left border indicator

  // Single blue palette for active appointments
  if (isUpcomingNext || isCurrent) {
    cardClasses += ` ${indicatorBorderClass} border-blue-500`; // blue indicator
    timeBoxClasses += " bg-blue-50 text-blue-800";
  } else if (isPast) {
    // No border, subtle background and opacity
    cardClasses += " bg-slate-50 opacity-75";
    timeBoxClasses += " bg-slate-100 text-slate-600";
  } else {
    // Other upcoming - subtle indicator
    cardClasses += ` ${indicatorBorderClass} border-blue-200`; // Lighter blue indicator
    timeBoxClasses += " bg-blue-50 text-blue-700";
  }

  return (
    <Card className={cardClasses}>
      <CardContent className="p-0">
        {" "}
        {/* Remove CardContent padding */}
        <div className="flex flex-col md:flex-row">
          {/* Time and Status Column */}
          <div className={timeBoxClasses}>
            {/* Session Type Badge: Softer style */}
            <div className="mb-2">
              {appointment.sessionType === "zoom" ? (
                <Badge
                  variant="secondary"
                  className={`text-base px-2 py-0.5 font-medium ${
                    isUpcomingNext || isCurrent
                      ? "bg-blue-100 text-blue-700"
                      : isOtherUpcoming
                      ? "bg-blue-100 text-blue-700"
                      : "bg-slate-200 text-slate-600"
                  }`}
                >
                  <Video className="h-4 w-4 mr-1" /> Zoom
                </Badge>
              ) : appointment.sessionType === "SP" ? (
                <Badge
                  variant="secondary"
                  className={`text-base px-2 py-0.5 font-medium ${
                    isUpcomingNext || isCurrent
                      ? "bg-blue-100 text-blue-700"
                      : isOtherUpcoming
                      ? "bg-blue-100 text-blue-700"
                      : "bg-slate-200 text-slate-600"
                  }`}
                >
                  <Video className="h-4 w-4 mr-1" /> Simple Practice
                </Badge>
              ) : (
                <Badge
                  variant="secondary"
                  className={`text-base px-2 py-0.5 font-medium ${
                    isUpcomingNext || isCurrent
                      ? "bg-blue-100 text-blue-700"
                      : isOtherUpcoming
                      ? "bg-blue-100 text-blue-700"
                      : "bg-slate-200 text-slate-600"
                  }`}
                >
                  <Phone className="h-4 w-4 mr-1" /> Phone
                </Badge>
              )}
            </div>

            {/* Time */}
            <div className="flex items-center mb-1.5">
              <Clock className="h-4 w-4 mr-1.5 flex-shrink-0" />
              {/* Slightly larger font for key time info */}
              <span
                className={`font-medium ${
                  isCurrent || isUpcomingNext ? "text-lg" : "text-lg"
                }`}
              >
                {formatTimeRange(appointment.startTime, appointment.endTime)}
              </span>
            </div>

            {/* Status Badges: Smaller, subtle */}
            {isCurrent && (
              <Badge
                variant="default"
                className="bg-blue-500 text-white text-sm px-2 py-0.5 font-semibold animate-pulse w-fit"
              >
                Current
              </Badge>
            )}
            {isUpcomingNext && (
              <Badge
                variant="default"
                className="bg-blue-500 text-white text-sm px-2 py-0.5 font-semibold w-fit"
              >
                Next Up
              </Badge>
            )}
            {/* Removed explicit 'Upcoming' badge for others to reduce clutter */}
            {isPast && (
              <Badge variant="secondary" className="text-sm px-2 py-0.5 w-fit">
                {appointment.disposition || "Completed"}
              </Badge>
            )}
          </div>

          {/* Patient Info and Actions Column */}
          <div className="flex-1 p-4">
            {" "}
            {/* Standard padding */}
            <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-3">
              {/* Patient Name */}
              {/* Back to more standard text sizes */}
              <h3
                className={`text-xl font-semibold mb-1 sm:mb-0 ${
                  isUpcomingNext || isCurrent
                    ? "text-blue-800"
                    : isPast
                    ? "text-slate-700"
                    : "text-gray-800" // Default for other upcoming
                }`}
              >
                {appointment.patientName}
              </h3>

              {/* Action Buttons: Grouped, smaller size */}
              <div className="flex flex-wrap gap-2 items-start">
                {appointment.sessionType === "zoom" ? (
                  <Button
                    size="xl" // Smaller button
                    className="bg-blue-600 hover:bg-blue-700 text-white transition-colors shadow-sm disabled:opacity-60 px-3 text-lg"
                    onClick={() => handleJoinZoomClick(appointment)}
                    aria-label={`Join Zoom for ${appointment.patientName}`}
                  >
                    <Video className="h-8 w-8 mr-1.5" /> Join
                    <ExternalLink className="h-8 w-8 ml-1 opacity-70" />
                  </Button>
                ) : appointment.sessionType === "SP" ? (
                  <Button
                    size="xl" // Smaller button
                    className="bg-blue-600 hover:bg-blue-700 text-white transition-colors shadow-sm disabled:opacity-60 px-3 text-lg"
                    onClick={() => handleSimplePracticeClick(appointment)}
                    aria-label={`Join Zoom for ${appointment.patientName}`}
                  >
                    <Video className="h-8 w-8 mr-1.5" /> Join
                    <ExternalLink className="h-8 w-8 ml-1 opacity-70" />
                  </Button>
                ) : (
                  // Phone Session
                  <div className="flex items-center gap-2">
                    <Button
                      size="xl"
                      variant="outline"
                      // Softer outline button
                      className={`border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors ${
                        isCopied === appointment.id
                          ? "bg-blue-50 border-blue-200"
                          : ""
                      } disabled:opacity-60 px-3`}
                      onClick={() =>
                        handleCopyPhone(appointment.phone, appointment.id)
                      }
                      disabled={isPast || !appointment.phone}
                      aria-label={`Copy phone for ${appointment.patientName}`}
                    >
                      {isCopied === appointment.id ? (
                        <CheckCircle className="h-4 w-4 text-blue-600" />
                      ) : (
                        <Copy className="h-8 w-8" />
                      )}
                      <span className="ml-1.5 text-lg font-mono">
                        {appointment.phone || "No Phone"}
                      </span>
                    </Button>
                    <Button
                      size="xl"
                      className="bg-blue-600 hover:bg-blue-700 text-white transition-colors shadow-sm disabled:opacity-60 px-3 text-lg"
                      disabled={!appointment.phone}
                      aria-label={`Call ${appointment.patientName}`}
                      onClick={() => {
                        const tryAppRedirect = (url, fallback) => {
                          const now = Date.now();
                          const timeout = setTimeout(() => {
                            if (Date.now() - now < 1500) {
                              fallback();
                            }
                          }, 1000); // Wait a bit to see if the app opens

                          window.location.href = url;
                        };

                        tryAppRedirect(
                          `rcapp://r/dialer?number=${appointment.phone}`,
                          () => {
                            tryAppRedirect(
                              `rcmobile://call?number=${appointment.phone}`,
                              () => {
                                window.open(
                                  `https://app.ringcentral.com/r/dialer?number=${appointment.phone.replaceAll(
                                    " ",
                                    ""
                                  )}`,
                                  "_blank"
                                );
                              }
                            );
                          }
                        );
                      }}
                    >
                      {" "}
                      <Phone className="h-8 w-8" />
                      <span className="ml-1.5 text-lg">Call</span>
                    </Button>
                  </div>
                )}
              </div>
            </div>
            {/* Lower Action Buttons: More prominent disposition button */}
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
              <Button
                size="lg"
                variant="ghost" // Ghost button for less emphasis
                className={`text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-3 disabled:opacity-60 text-base ${
                  isPast ? "opacity-80" : ""
                }`}
                onClick={() => handlePatientDetailsClick(appointment)}
                aria-label={`Details for ${appointment.patientName}`}
                disabled={appointment.sessionType === "SP"}
              >
                {" "}
                <User className="h-8 w-8 mr-1" /> Details{" "}
              </Button>

              {/* More prominent disposition button */}
              {/* <Button
                size="lg"
                className={`bg-indigo-100 text-indigo-700 hover:bg-indigo-200 hover:text-indigo-800 px-3 text-base ${
                  isPast ? "opacity-80" : ""
                } ${
                  appointment.isDispositionAdded === 1
                    ? "bg-green-100 text-green-700 hover:bg-green-200 hover:text-green-800"
                    : "bg-indigo-100 text-indigo-700 hover:bg-indigo-200 hover:text-indigo-800"
                }`}
                onClick={() => handleDispositionClick(appointment)}
                aria-label={`Disposition for ${appointment.patientName}`}
                disabled={appointment.sessionType === "SP"}
              >
                {" "}
                <Clipboard className="h-8 w-8 mr-1" /> Update Disposition{" "}
              </Button> */}
              <Button
                size="lg"
                className={`bg-indigo-100 text-indigo-700 hover:bg-indigo-200 hover:text-indigo-800 px-3 text-base ${
                  isPast ? "opacity-80" : ""
                } ${
                  appointment.isDispositionAdded === 1
                    ? "bg-green-100 text-green-700 hover:bg-green-200 hover:text-green-800"
                    : "bg-indigo-100 text-indigo-700 hover:bg-indigo-200 hover:text-indigo-800"
                }`}
                onClick={() => handleDispositionClick(appointment)}
                aria-label={`Disposition for ${appointment.patientName}`}
                // disabled={appointment.sessionType === "SP"}
              >
                {appointment.isDispositionAdded === 1 ? (
                  <>
                    <CheckCircle className="h-4 w-4 mr-1.5 text-green-600" />
                    Disposition Added
                  </>
                ) : (
                  <>
                    <Clipboard className="h-4 w-4 mr-1.5" />
                    Add Disposition
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// --- Main Component ---
const Schedule = () => {
  const therapist = useSelector((store) => store.therapist);
  const [searchParams, setSearchParams] = useSearchParams();

  // 2️⃣ Compute initial date from the URL (or default to today)
  const initialDate = useMemo(() => {
    const dateParam = searchParams.get("date");
    const parsed = dateParam ? new Date(dateParam) : new Date();
    return isNaN(parsed) ? new Date() : parsed;
  }, [searchParams]);

  // --- State Variables ---
  const [selectedDate, setSelectedDate] = useState(initialDate); // Default to today's date
  const [appointments, setAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [
    loadingMissedDispostionAppointments,
    setLoadingMissedDispostionAppointments,
  ] = useState(false);
  const [dispositionDialogOpen, setDispositionDialogOpen] = useState(false);
  const [patientDetailsDialogOpen, setPatientDetailsDialogOpen] =
    useState(false);
  const [patientDetailsDialogLoader, setPatientDetailsDialogLoader] =
    useState(true);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [disposition, setDisposition] = useState("");
  const [dispositionNotes, setDispositionNotes] = useState("");
  const [therapistNoteText, setTherapistNoteText] = useState("");
  const [isCopied, setIsCopied] = useState(null);

  const [loadingLead, setLoadingLead] = useState(false);
  const [leadError, setLeadError] = useState(null);
  const [leadInfo, setLeadInfo] = useState(null); // stores fetched lead data
  const [fileteredActivityEvents, setFilteredActivityEvents] = useState([]);
  const [isDisabled, setIsDisabled] = useState(false);
  const [showingPendingOnly, setShowingPendingOnly] = useState(false);
  const [missedDispositionsCount, setMissedDispositionsCount] = useState(0);
  const [missedDispositionAppointments, setMissedDispositionsAppointments] =
    useState([]);
  const [verifyInsurance, setVerifyInsurance] = useState(false);
  const [modality, setModality] = useState("none");
  const [diagnosisCode, setDiagnosisCode] = useState("none");
  const [searchDiagnosisTerm, setSearchDiagnosisTerm] = useState("");

  useEffect(() => {
    setSearchParams(
      { date: formatDateFns(selectedDate, "yyyy-MM-dd") },
      { replace: true }
    );
  }, [selectedDate, setSearchParams]);

  const processGoogleEvent = (googleEvent) => {
    const eventObj = {
      id: "",
      patientName: "",
      patientEmail: "",
      startTime: "",
      endTime: "",
      sessionType: "",
      zoomLink: "",
      disposition: "",
      notes: "",
      phone: "",
      isDispositionAdded: "Not Available",
    };
    eventObj.startTime = new Date(googleEvent.start.dateTime).toISOString();
    eventObj.endTime = new Date(googleEvent.end.dateTime).toISOString();
    eventObj.id = googleEvent.id;
    eventObj.sessionType = "SP";
    eventObj.zoomLink = googleEvent.location;
    eventObj.patientName = googleEvent.summary.replace("Appointment", "");
    eventObj.patientName = eventObj.patientName.replace("🔒 ", "");
    eventObj.isDispositionAdded = googleEvent.is_disposition_added;
    return eventObj;
  };

  const processEvent = (calendlyEvent) => {
    const eventObj = {
      id: "",
      patientName: "",
      patientEmail: "",
      startTime: "",
      endTime: "",
      sessionType: "",
      zoomLink: "",
      disposition: "",
      notes: "",
      phone: "",
      isDispositionAdded: "Not Available",
    };
    eventObj.startTime = calendlyEvent.start_time;
    eventObj.endTime = calendlyEvent.end_time;
    eventObj.id = calendlyEvent.uri;
    if (calendlyEvent?.location?.type === "zoom") {
      eventObj.sessionType = "zoom";
      eventObj.zoomLink = calendlyEvent.location;
    } else {
      eventObj.sessionType = "phone";
    }
    const invitee = calendlyEvent.invitees[0];
    if (invitee) {
      eventObj.patientName = invitee.name;
      eventObj.patientEmail = invitee.email;
      if (eventObj.sessionType === "phone") {
        eventObj.phone = invitee.questions_and_answers[0]?.answer;
      }
    }
    if (calendlyEvent.hasOwnProperty("is_disposition_added")) {
      eventObj.isDispositionAdded = calendlyEvent.is_disposition_added;
    }
    return eventObj;
  };

  const fetchCalendlyEvents = async (startTime, endTime, showLoader = true) => {
    if (showLoader) setLoadingAppointments(true);
    try {
      let email = therapist.email;
      if (therapist.therapist_id === "9801c330-40a1-70a9-06e0-fe8965f5b86c") {
        email = "geetamagesh@therawin.health";
        // email = "geetanjalivij@therawin.health";
        // email = "mariamali@therawin.health";
        // email = "meerab@therawin.health";
      }
      let [calendlyData, SPData] = await Promise.all([
        getCalendlyEvents(startTime, endTime, email),
        getSimplePracticeEvents(startTime, endTime, email),
      ]);
      calendlyData = calendlyData.map((da) => processEvent(da));
      SPData = SPData.map((da) => processGoogleEvent(da));
      let data = [...calendlyData, ...SPData];
      setAppointments(data);
    } catch (err) {
      console.error(err);
    } finally {
      if (showLoader) setLoadingAppointments(false);
    }
  };

  const fetchMissedDispositions = async (showLoader = true) => {
    if (showLoader) setLoadingMissedDispostionAppointments(true);
    try {
      let therapistId = therapist.therapist_id;
      if (therapist.therapist_id === "9801c330-40a1-70a9-06e0-fe8965f5b86c") {
        // therapistId = "78a1d380-10b1-70ef-dbce-b3bf64c73177";
        therapistId = "f831a310-7061-70ea-f4e6-f75ffee16041";
        // therapistId = "78714380-a001-7099-61c3-71207ed6b094";
        // therapistId = "6821a3f0-a011-70de-bf18-74174cc95273";
      }
      let data = await getMissedEvents(therapistId);
      data = data.map((da) => processEvent(da));
      setMissedDispositionsCount(data.length);
      setMissedDispositionsAppointments(data);
    } catch (err) {
      console.error(err);
    } finally {
      if (showLoader) setLoadingMissedDispostionAppointments(false);
    }
  };

  useEffect(() => {
    if (selectedDate) {
      const startTime = startOfDay(selectedDate).toISOString();
      const endTime = endOfDay(selectedDate).toISOString();
      fetchCalendlyEvents(startTime, endTime, true); // Show loader on initial load

      const interval = setInterval(() => {
        fetchCalendlyEvents(startTime, endTime, false); // Auto-refresh silently
      }, 30000);

      return () => clearInterval(interval); // Cleanup
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchMissedDispositions(true);

    const interval = setInterval(() => {
      fetchMissedDispositions(false); // Auto-refresh silently
    }, 30000);

    return () => clearInterval(interval); // Cleanup
  }, []);

  // useEffect(() => {
  //   diagnosisOptions.filter((option) =>
  //     option.label.toLowerCase().includes(searchDiagnosisTerm.toLowerCase())
  //   );
  // }, [searchDiagnosisTerm, diagnosisOptions]);

  // --- Event Handlers (Mostly Unchanged) ---
  const handleDispositionClick = async (appointment) => {
    setSelectedAppointment(appointment);
    setDispositionDialogOpen(true);
    setLoadingLead(true);
    setLeadError(null);
    setLeadInfo(null);

    try {
      if (appointment.sessionType === "SP") {
        const data = await getLeadFromName(
          appointment.patientName.split(" ")[0]
        );
        const lead = data.lead;
        appointment.patientEmail = lead.EmailAddress;
      }
      const email = appointment.patientEmail;
      const data = await getLeadInfoAPI(email);
      setLeadInfo(data["lead"]); // store lead info (e.g., stage, name, stage)
      const filteredEvents = activityEvents.filter((event) =>
        event.allowedStages.includes(data["lead"].ProspectStage)
      );
      setFilteredActivityEvents(filteredEvents);
    } catch (error) {
      console.error("Error fetching lead info:", error);
      setLeadError(
        error.response?.data?.message ||
          error.message ||
          "Something went wrong."
      );
    } finally {
      setLoadingLead(false);
    }
  };

  const handlePatientDetailsClick = async (appointment) => {
    setPatientDetailsDialogLoader(true);
    setSelectedAppointment(appointment);
    setPatientDetailsDialogOpen(true);
    const data = await getLeadActivityReport(appointment.patientEmail);
    const sortedActivities = sortActivitiesByTime(data);
    appointment.activities = sortedActivities;
    const notesRaw = await getAllTherapistNotesForPatient(
      appointment.patientEmail
    );
    appointment.therapistNotes = Array.isArray(notesRaw)
      ? notesRaw
      : notesRaw?.notes || [];
    setSelectedAppointment(appointment);
    setPatientDetailsDialogLoader(false);
  };

  const resetDispositionForm = () => {
    setDisposition("");
    setDispositionNotes("");
    setSelectedAppointment(null);
    setLeadInfo(null);
    setLeadError(null);
    setVerifyInsurance(false); // boolean not string
    setModality("none");
    setDiagnosisCode("none");
    setIsDisabled(false);
    setHasBookedFFS("no");
  };

  const handleSendSlackAlert = async () => {
    try {
      setIsDisabled(true);
      const appointmentData = selectedAppointment;
      appointmentData["therapist"] = {
        therapist_id: therapist.therapist_id,
        therapist_email: therapist.email,
      };
      await sendSlackMessagePaidDisposition(
        selectedAppointment?.patientName,
        disposition,
        appointmentData
      );
      toast.success("Disposition saved successfully.");
      setDispositionDialogOpen(false);
      resetDispositionForm();
    } catch (error) {
      console.log(error);
      toast.error(
        error?.response?.data?.message ||
          "An error occurred while saving the disposition."
      );
      setIsDisabled(false);
    }
  };

  const handleSaveDisposition = async () => {
    setIsDisabled(true);
    const now = new Date();
    const activityUTCDateTime = `${now.getUTCFullYear()}-${String(
      now.getUTCMonth() + 1
    ).padStart(2, "0")}-${String(now.getUTCDate()).padStart(2, "0")} ${String(
      now.getUTCHours()
    ).padStart(2, "0")}:${String(now.getUTCMinutes()).padStart(
      2,
      "0"
    )}:${String(now.getUTCSeconds()).padStart(2, "0")}`;

    const fullNote = `${dispositionNotes || ""}\nBy Therapist: ${
      therapist?.name || "N/A"
    }`;

    let metadata = "";
    if (
      disposition === "223" ||
      disposition === "224" ||
      disposition === "230" ||
      disposition === "233"
    ) {
      metadata = `Session Start Time UTC: ${
        selectedAppointment?.startTime || "N/A"
      }&& Session End Time UTC: ${selectedAppointment?.endTime || "N/A"}`;
    }

    const activityData = {
      leadId: leadInfo.ProspectID,
      activityEventId: disposition,
      activityNote: fullNote.trim(),
      activityUTCDateTime,
      customFields: [
        {
          SchemaName: "mx_Custom_1",
          Value: metadata,
        },
      ],
    };

    const appointmentData = selectedAppointment;
    appointmentData["therapist"] = {
      therapist_id: therapist.therapist_id,
      therapist_email: therapist.email,
    };

    const insuranceNote = `Modality: ${modality}\nDiagnosis Code: ${diagnosisCode}`;
    const insuranceData = {
      isInterested: verifyInsurance,
      insuranceNote: insuranceNote.trim(),
    };

    try {
      if (activityData.activityEventId) {
        setDispositionDialogOpen(false);

        await createActivityOnLeadAPI(
          activityData.leadId,
          activityData.activityEventId,
          activityData.activityNote,
          activityData.activityUTCDateTime,
          activityData.customFields,
          appointmentData,
          insuranceData
        );

        setMissedDispositionsAppointments((prev) =>
          prev.map((appt) =>
            appt.id === selectedAppointment.id
              ? { ...appt, isDispositionAdded: 1 }
              : appt
          )
        );

        setAppointments((prev) =>
          prev.map((appt) =>
            appt.id === selectedAppointment.id
              ? { ...appt, isDispositionAdded: 1 }
              : appt
          )
        );
      }

      if (therapistNoteText.trim()) {
        await createTherapistNoteAPI({
          email: selectedAppointment.patientEmail,
          note: therapistNoteText.trim(),
          therapistId: therapist.therapist_id,
        });
        setTherapistNoteText("");
      }

      toast.success("Disposition saved successfully.");

      resetDispositionForm();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "An error occurred while saving the disposition."
      );
      setIsDisabled(false);
    }
  };

  const handleCopyPhone = (phone, appointmentId) => {
    navigator.clipboard.writeText(phone);
    setIsCopied(appointmentId);
    setTimeout(() => setIsCopied(null), 2000);
  };

  const handleJoinZoomClick = (appointment) => {
    if (appointment.zoomLink) {
      window.open(appointment.zoomLink.join_url, "_blank");
      handleDispositionClick(appointment);
    }
  };

  const handleSimplePracticeClick = (appointment) => {
    if (appointment.zoomLink) {
      const urlObj = new URL(appointment.zoomLink);
      urlObj.searchParams.set("auto_auth", "true");
      urlObj.searchParams.delete("origin");
      window.open(urlObj.toString(), "_blank");
    }
  };

  const handleGoToToday = () => {
    setSelectedDate(new Date());
  };

  // Notification Click Handler: Clears date and shows only pending items
  const handleNotificationClick = () => {
    setShowingPendingOnly(true); // Enter pending view mode
  };

  // Handler to exit pending view and go back to calendar view (e.g., showing today)
  const handleShowCalendarView = () => {
    setShowingPendingOnly(false);
  };

  // --- Derived State & Logic ---
  const displayedAppointments = useMemo(() => {
    if (!selectedDate) return [];
    const targetDateStart = startOfDay(selectedDate);
    const filtered = appointments.filter(
      (a) =>
        a.startTime &&
        !isNaN(new Date(a.startTime).getTime()) &&
        startOfDay(new Date(a.startTime)).getTime() ===
          targetDateStart.getTime()
    );
    let finalFiltered = filtered;
    if (isToday(selectedDate)) {
      // Only show current/upcoming if viewing today
      finalFiltered = filtered.filter((a) => {
        const status = getAppointmentStatus(a.startTime, a.endTime);
        return (
          status === "current" || status === "upcoming" || status === "past"
        );
      });
    }
    return sortAppointmentsByTime(finalFiltered);
  }, [appointments, selectedDate]);

  const upcomingAppointmentId = useMemo(() => {
    const now = new Date();
    return displayedAppointments.find((a) => new Date(a.startTime) > now)?.id;
  }, [displayedAppointments]);

  const handleDiagnosisCode = (event) => {
    console.log(event);
    setDiagnosisCode(event);
    setSearchDiagnosisTerm(event);
  };

  const [hasBookedFFS, setHasBookedFFS] = useState("no");

  function useDebouncedValue(value, delay = 300) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
      const handler = setTimeout(() => setDebounced(value), delay);
      return () => clearTimeout(handler);
    }, [value, delay]);
    return debounced;
  }

  const debouncedSearch = useDebouncedValue(searchDiagnosisTerm, 300);

  // --- Main Render ---
  return (
    // Use a simpler, lighter background
    <div className="min-h-screen bg-slate-50 p-4 md:p-6 h-full w-full">
      {/* Reduced max-width */}
      <div className="max-w-6xl mx-auto">
        {/* Reduced header padding */}
        {!showingPendingOnly ? (
          <header className="mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-100">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              {/* Personalized, friendly title */}
              <div>
                <h1 className="text-2xl font-semibold text-gray-800">
                  Hello {therapist.name}! Here's your schedule
                </h1>
                <p className="text-gray-600 mt-1 text-base">
                  {selectedDate
                    ? formatDateFns(selectedDate, "MMMM d, yyyy")
                    : ""}
                  {selectedDate && isToday(selectedDate) && (
                    <Badge
                      variant="outline"
                      className="ml-2 text-sm border-blue-200 text-blue-600 bg-blue-50"
                    >
                      Today
                    </Badge>
                  )}
                </p>
              </div>

              {/* Date Picker Area */}
              <div className="flex items-center gap-2 flex-wrap">
                {missedDispositionsCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-amber-600 hover:bg-amber-100 hover:text-amber-700 text-base"
                    onClick={handleNotificationClick}
                    aria-label={`View ${missedDispositionsCount} missed dispositions`}
                    title={`View ${missedDispositionsCount} missed dispositions`}
                  >
                    <Bell className="h-4 w-4 mr-1.5 animate-pulse" />
                    Missed dispositions: {missedDispositionsCount}
                  </Button>
                )}

                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      size="sm" // Smaller date picker button
                      variant={"outline"}
                      className={`w-[240px] justify-start text-left font-normal text-base border-gray-300 focus-visible:ring-blue-400 focus-visible:ring-offset-0 ${
                        !selectedDate && "text-muted-foreground"
                      }`}
                    >
                      <CalendarIcon className="mr-2 h-8 w-8 text-blue-600" />
                      {selectedDate ? (
                        formatDateFns(selectedDate, "MMM d, yyyy")
                      ) : (
                        <span>Select date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <CalendarComponent
                      className="text-base"
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                    />
                  </PopoverContent>
                </Popover>

                {/* "Go to Today" Button - Hidden when today IS selected (Req 2) */}
                {selectedDate && !isToday(selectedDate) && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleGoToToday}
                    aria-label="Go to today's date"
                    className="text-base border-gray-300 text-blue-600 hover:bg-blue-50"
                  >
                    {" "}
                    <CalendarCheck className="h-8 w-8 mr-1.5" /> Today{" "}
                  </Button>
                )}
              </div>
            </div>
          </header>
        ) : (
          <header className="mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-100">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              {/* Personalized, friendly title */}
              <div>
                <h1 className="text-2xl font-semibold text-gray-800">
                  Missed Dispositions
                </h1>
                <p className="text-amber-700 mt-1 text-base">
                  Showing all past appointments needing dispositions.
                </p>
              </div>

              {/* Date Picker Area */}
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-blue-600 border-blue-300 hover:bg-blue-50 text-base"
                  onClick={handleShowCalendarView}
                >
                  <ListFilter className="h-8 w-8 mr-1.5" /> Show Calendar View
                </Button>

                {/* "Go to Today" Button - Hidden when today IS selected (Req 2) */}
                {selectedDate && !isToday(selectedDate) && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleGoToToday}
                    aria-label="Go to today's date"
                    className="text-base border-gray-300 text-blue-600 hover:bg-blue-50"
                  >
                    {" "}
                    <CalendarCheck className="h-8 w-8 mr-1.5" /> Today{" "}
                  </Button>
                )}
              </div>
            </div>
          </header>
        )}
        {!showingPendingOnly && (
          <>
            {loadingAppointments || loadingMissedDispostionAppointments ? (
              <div className="bg-white rounded-lg p-8 text-center border border-gray-100 shadow-sm mt-6">
                <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-gray-700 mb-2">
                  Loading appointments...
                </h3>
                <p className="text-base text-gray-500">
                  Please wait while we fetch the data.
                </p>
              </div>
            ) : displayedAppointments.length === 0 ? (
              <div className="bg-white rounded-lg p-8 text-center border border-gray-100 shadow-sm mt-6">
                <Smile className="h-16 w-16 text-blue-300 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-gray-700 mb-2">
                  No appointments found for this date.
                </h3>
                <p className="text-base text-gray-500">
                  Try selecting a different date.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {displayedAppointments.map((appointment) => (
                  <RenderAppointmentCard
                    key={appointment.id}
                    appointment={appointment}
                    upcomingAppointmentId={upcomingAppointmentId}
                    handleJoinZoomClick={handleJoinZoomClick}
                    isCopied={isCopied}
                    handleCopyPhone={handleCopyPhone}
                    handlePatientDetailsClick={handlePatientDetailsClick}
                    handleDispositionClick={handleDispositionClick}
                    handleSimplePracticeClick={handleSimplePracticeClick}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Missed Disposition Appointments – only if showPendingOnly is true */}
        {showingPendingOnly && (
          <>
            {loadingAppointments ? (
              <div className="bg-white rounded-lg p-8 text-center border border-gray-100 shadow-sm mt-6">
                <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-gray-700 mb-2">
                  Loading appointments with missed dispositions...
                </h3>
                <p className="text-base text-gray-500">
                  Please wait while we fetch the data.
                </p>
              </div>
            ) : missedDispositionAppointments.length === 0 ? (
              <div className="bg-white rounded-lg p-8 text-center border border-gray-100 shadow-sm mt-6">
                <Smile className="h-16 w-16 text-blue-300 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-gray-700 mb-2">
                  No missed dispositions for appointments found.
                </h3>
              </div>
            ) : (
              <div className="space-y-4">
                {missedDispositionAppointments
                  .filter((appointment) => appointment.isDispositionAdded === 0)
                  .map((appointment) => (
                    <RenderAppointmentCard
                      key={appointment.id}
                      appointment={appointment}
                      upcomingAppointmentId={upcomingAppointmentId}
                      handleJoinZoomClick={handleJoinZoomClick}
                      isCopied={isCopied}
                      handleCopyPhone={handleCopyPhone}
                      handlePatientDetailsClick={handlePatientDetailsClick}
                      handleDispositionClick={handleDispositionClick}
                      handleSimplePracticeClick={handleSimplePracticeClick}
                    />
                  ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* --- Dialogs (Reduced Font Size, Consistent Styling) --- */}
      <Dialog
        open={dispositionDialogOpen}
        onOpenChange={(open) => {
          setDispositionDialogOpen(open);
          if (!open) {
            resetDispositionForm(); // <-- reset on close
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold text-blue-900">
              Set Appointment Disposition
            </DialogTitle>
            <DialogDescription className="text-lg text-gray-600">
              Record outcome for {selectedAppointment?.patientName}
            </DialogDescription>
          </DialogHeader>

          {loadingLead ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-600" />
            </div>
          ) : leadError && selectedAppointment?.sessionType !== "SP" ? (
            <div className="text-red-600 text-lg font-medium py-4">
              {leadError}
            </div>
          ) : leadError && selectedAppointment?.sessionType === "SP" ? (
            <div>
              <div className="text-lg text-gray-700 font-medium mb-2">
                Lead Stage:{" "}
                <span className="text-blue-800">Pending Conversion/Paid</span>
              </div>
              <div className="grid gap-4 py-4">
                <div className="space-y-1.5">
                  <label
                    htmlFor="disposition"
                    className="text-lg font-medium text-gray-700"
                  >
                    Disposition
                  </label>
                  <Select value={disposition} onValueChange={setDisposition}>
                    <SelectTrigger
                      id="disposition"
                      className="text-base focus:ring-blue-500"
                    >
                      <SelectValue placeholder="Select disposition" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem
                        key={254}
                        value={String(254)}
                        className="text-base"
                      >
                        Paid session done
                      </SelectItem>
                      <SelectItem
                        key={255}
                        value={String(255)}
                        className="text-base"
                      >
                        Paid session missed
                      </SelectItem>
                      <SelectItem
                        key={244}
                        value={String(244)}
                        className="text-base"
                      >
                        1st paid session done
                      </SelectItem>
                      <SelectItem
                        key={247}
                        value={String(247)}
                        className="text-base"
                      >
                        1st paid session missed
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => {
                    resetDispositionForm();
                    setDispositionDialogOpen(false);
                  }}
                  className="text-base"
                >
                  Cancel
                </Button>{" "}
                {/* Reduced size */}
                <Button
                  size="lg"
                  onClick={handleSendSlackAlert}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-base"
                  disabled={isDisabled}
                >
                  Save
                </Button>{" "}
                {/* Reduced size */}
              </DialogFooter>
            </div>
          ) : (
            <div>
              {leadInfo?.ProspectStage && (
                <div className="text-lg text-gray-700 font-medium mb-2">
                  Lead Stage:{" "}
                  <span className="text-blue-800">
                    {leadInfo.ProspectStage}
                  </span>
                </div>
              )}
              <div className="grid gap-4 py-4">
                {/* Reduced gap/padding */}
                <div className="space-y-1.5">
                  <label
                    htmlFor="disposition"
                    className="text-lg font-medium text-gray-700"
                  >
                    Disposition
                  </label>{" "}
                  {/* Reduced size */}
                  <Select value={disposition} onValueChange={setDisposition}>
                    <SelectTrigger
                      id="disposition"
                      className="text-base focus:ring-blue-500"
                    >
                      <SelectValue placeholder="Select disposition" />
                    </SelectTrigger>
                    <SelectContent>
                      {fileteredActivityEvents.map((event) => (
                        <SelectItem
                          key={event.id}
                          value={String(event.id)}
                          className="text-base"
                        >
                          {event.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* Remove one zero to enable it for FFS */}
                {disposition === "230" && (
                  <div className="p-3 bg-blue-50 rounded-md border border-blue-100">
                    <div className="flex items-center gap-8 justify-between">
                      <label className="text-base font-medium text-blue-800 flex-shrink-0 flex-grow">
                        Should we go ahead with their insurance verification?
                      </label>
                      <RadioGroup
                        value={verifyInsurance}
                        onValueChange={setVerifyInsurance}
                        className="flex gap-8 flex-shrink-0"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value={true} id="verification-yes" />
                          <Label
                            htmlFor="verification-yes"
                            className="text-base"
                          >
                            Yes
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value={false} id="verification-no" />
                          <Label
                            htmlFor="verification-no"
                            className="text-base"
                          >
                            No
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>
                  </div>
                )}
                {/* Additional fields when verification is "yes" */}
                {disposition === "230" && verifyInsurance && (
                  <div className="p-3 bg-blue-50/50 rounded-md border border-blue-100">
                    <div className="grid grid-cols-2 gap-8">
                      {/* Modality Dropdown */}
                      <div className="space-y-1">
                        <Label
                          htmlFor="modality"
                          className="text-base font-medium text-gray-700"
                        >
                          Modality (Optional)
                        </Label>
                        <Select value={modality} onValueChange={setModality}>
                          <SelectTrigger
                            id="modality"
                            className="text-base focus:ring-blue-500 bg-white h-9"
                          >
                            <SelectValue placeholder="Select modality" />
                          </SelectTrigger>
                          <SelectContent>
                            {modalityOptions.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                                className={`text-base ${
                                  option.italic ? "italic text-gray-500" : ""
                                }`}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Primary Diagnosis Code - Regular Dropdown */}
                      <div className="space-y-1">
                        <Label
                          htmlFor="diagnosis"
                          className="text-base font-medium text-gray-700"
                        >
                          Diagnosis Code (Optional)
                        </Label>
                        <Select
                          value={diagnosisCode}
                          onValueChange={setDiagnosisCode}
                          onKeyDown={(e) => {
                            // Prevent default keyboard typeahead
                            e.stopPropagation();
                          }}
                        >
                          <SelectTrigger
                            id="diagnosis"
                            className="text-base focus:ring-blue-500 bg-white h-9"
                          >
                            <SelectValue placeholder="Select diagnosis code" />
                          </SelectTrigger>
                          <SelectContent>
                            {diagnosisOptions.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                                className={`text-base ${
                                  option.italic ? "italic" : ""
                                } ${option.muted ? "text-gray-500" : ""}`}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}
                {disposition === "223" && (
                  <div className="p-3 bg-green-50 rounded-md border border-green-100 space-y-4">
                    {/* Therapist Calendar Buttons */}
                    <div className="space-y-2">
                      <Label className="text-base font-medium text-green-800">
                        Book FFS session with a therapist:
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {THERAPIST_FFS_EVENTS.map((FFS_event) => (
                          <a
                            href={FFS_event.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button
                              type="button"
                              variant="outline"
                              className="bg-white border-green-300 text-green-700 hover:bg-green-50 text-base"
                            >
                              <CalendarIcon className="mr-1 h-4 w-4" />
                              {FFS_event.name}
                              <ExternalLink className="ml-1 h-3 w-3" />
                            </Button>
                          </a>
                        ))}
                      </div>
                      <p className="text-sm text-green-700 italic mt-1">
                        Click on a therapist's name to open their calendar in a
                        new tab
                      </p>
                    </div>

                    <div className="border-t border-green-200 pt-3">
                      <div className="flex items-center gap-8 justify-between">
                        <Label className="text-base font-medium text-green-800 flex-shrink-0 flex-grow">
                          Have you booked FFS session for the client?
                        </Label>
                        <RadioGroup
                          value={hasBookedFFS}
                          onValueChange={setHasBookedFFS}
                          className="flex gap-8 flex-shrink-0"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="yes" id="ffs-booked-yes" />
                            <Label
                              htmlFor="ffs-booked-yes"
                              className="text-base"
                            >
                              Yes
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="no" id="ffs-booked-no" />
                            <Label
                              htmlFor="ffs-booked-no"
                              className="text-base"
                            >
                              No
                            </Label>
                          </div>
                        </RadioGroup>
                      </div>
                    </div>
                  </div>
                )}
                <div className="space-y-1.5">
                  <label
                    htmlFor="notes"
                    className="text-lg font-medium text-gray-700"
                  >
                    Additional Notes
                  </label>{" "}
                  {/* Reduced size */}
                  <Textarea
                    id="notes"
                    placeholder="Enter additional notes..."
                    value={dispositionNotes}
                    onChange={(e) => setDispositionNotes(e.target.value)}
                    className="min-h-[100px] text-lg focus:ring-blue-500 focus:border-blue-500" /* Reduced size */
                  />
                </div>

                {/* Therapist Confidential Notes Section */}
                <div className="space-y-1 border-t pt-3 mt-2 border-dashed border-slate-300">
                  <label
                    htmlFor="therapist-notes"
                    className="text-lg font-medium text-indigo-700 flex items-center gap-1.5"
                  >
                    <FileText className="h-4 w-4" /> Confidential Therapist Note
                  </label>
                  <p className="text-xs text-slate-500">
                    This note is confidential and linked to this session.
                  </p>
                  <Textarea
                    id="therapist-notes"
                    placeholder="Add confidential notes for your records..."
                    value={therapistNoteText}
                    onChange={(e) => setTherapistNoteText(e.target.value)}
                    className="min-h-[100px] text-sm focus:ring-indigo-500 focus:border-indigo-500 bg-indigo-50/30"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => {
                    resetDispositionForm();
                    setDispositionDialogOpen(false);
                  }}
                  className="text-base"
                >
                  Cancel
                </Button>{" "}
                {/* Reduced size */}
                <Button
                  size="lg"
                  onClick={handleSaveDisposition}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-base"
                  disabled={
                    isDisabled ||
                    !disposition ||
                    (disposition === "223" && hasBookedFFS === "no")
                  }
                >
                  Save
                </Button>{" "}
                {/* Reduced size */}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={patientDetailsDialogOpen}
        onOpenChange={setPatientDetailsDialogOpen}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold text-blue-900">
              Patient Details
            </DialogTitle>{" "}
            {/* Reduced size */}
            <DialogDescription className="text-lg text-gray-600">
              {" "}
              Information for {selectedAppointment?.patientName}{" "}
            </DialogDescription>{" "}
            {/* Reduced size */}
          </DialogHeader>
          {patientDetailsDialogLoader ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-600" />
            </div>
          ) : (
            <div className="space-y-4 py-4">
              {" "}
              {/* Reduced gap/padding */}
              <div>
                <h3 className="text-lg font-medium text-gray-600 mb-1">
                  Patient Activities
                </h3>{" "}
                {/* Reduced size */}
                <p className="text-base text-gray-700 max-h-80 overflow-y-auto flex flex-col">
                  {" "}
                  {/* Reduced size */}
                  {selectedAppointment?.activities.map((activity) => (
                    <div className="flex flex-col gap-1 bg-slate-100 rounded border border-slate-200  p-2.5 m-2">
                      <div className="flex justify-between items-center">
                        <div className="text-blue-600 font-semibold">
                          {activity.EventName}
                        </div>
                        <div className="text-blue-600 font-semibold">
                          {formatDateFns(
                            zonedTimeToUtc(
                              mysqlDatetimeToJavascriptDatetime(
                                activity.CreatedOn
                              ),
                              "America/Los_Angeles"
                            ),
                            "MMM dd, yyyy, p"
                          )}
                        </div>
                      </div>
                      <div className="break-words text-base">
                        {activity.Description || "No description"}
                      </div>
                    </div>
                  ))}
                </p>
              </div>
              {/* Confidential Therapist Notes Section */}
              <div>
                <h3 className="text-sm font-medium text-indigo-700 mb-2 flex items-center gap-1.5">
                  <FileText className="h-4 w-4" /> Confidential Notes History
                </h3>
                {!selectedAppointment?.therapistNotes ||
                selectedAppointment.therapistNotes.length === 0 ? (
                  <p className="text-sm text-gray-500 italic px-2">
                    No confidential therapist notes found for this client.
                  </p>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {selectedAppointment.therapistNotes.map((note, index) => (
                      <div
                        key={index}
                        className="text-sm p-3 bg-indigo-50/50 rounded border border-indigo-100 shadow-sm"
                      >
                        <p className="text-gray-800 mb-1.5 whitespace-pre-wrap">
                          {note.note_text}
                        </p>
                        <p className="text-xs text-indigo-600">
                          Noted by {note.name} on{" "}
                          {format(
                            new Date(note.updated_at),
                            "MMM d, yyyy, hh:mm:ss a"
                          )}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white text-base"
              onClick={() => setPatientDetailsDialogOpen(false)}
            >
              Close
            </Button>{" "}
            {/* Reduced size */}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Schedule;
